import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Tile, Heading } from '@carbon/react';
import { Warning } from '@carbon/icons-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            padding: '2rem',
            backgroundColor: '#f4f4f4',
          }}
        >
          <Tile
            style={{
              maxWidth: '500px',
              padding: '2rem',
              textAlign: 'center',
              borderRadius: '8px',
            }}
          >
            <Warning
              size={48}
              style={{
                color: '#78a9ff',
                marginBottom: '1rem',
              }}
            />

            <Heading
              style={{
                marginBottom: '0.5rem',
                fontSize: '1.5rem',
                color: 'var(--cds-text-primary)',
                fontFamily: 'var(--cds-font-family-sans)',
              }}
            >
              Something went wrong
            </Heading>

            <p
              style={{
                color: 'var(--cds-text-secondary)',
                marginBottom: '1.5rem',
                fontFamily: 'var(--cds-font-family-sans)',
              }}
            >
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>

            <Button
              onClick={this.handleReset}
              style={{
                backgroundColor: '#0f62fe',
                borderColor: '#0f62fe',
              }}
            >
              Refresh Page
            </Button>
          </Tile>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
